import type {
  TaskDueFilter,
  TaskOwnershipFilter,
} from '@src/features/tasks/types';
import { SidePanelContent } from '@src/shared/ui/SidePanel';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import TasksFilter from './TasksFilter';
import TasksList from './TasksList';
import TasksOwnershipFilter from './TasksOwnershipFilter';

export type TasksProps = {
  /**
   * Section whose tasks to show, e.g. "absence" -- the same name the reference
   * resolvers are registered under. Omitted, the panel reaches every open task
   * the viewer can reach, which is what the landing and dashboard pages want,
   * and offers the overdue/upcoming filter to cut that down.
   *
   * Scoped to a section the list is short enough to leave whole, but it holds
   * two kinds of row -- what the viewer has to answer and what they asked
   * somebody else -- so it offers the ownership filter in the same slot
   * instead.
   */
  referenceType?: string;
};

export default function Tasks({ referenceType }: TasksProps) {
  const { t } = useTranslation();
  const [dueFilter, setDueFilter] = useState<TaskDueFilter>('overdue');
  const [ownership, setOwnership] = useState<TaskOwnershipFilter>('all');
  const showDueFilter = !referenceType;

  return (
    <SidePanelContent
      title={t('common.tasks', 'Tasks')}
      headerContent={
        showDueFilter ? (
          <TasksFilter value={dueFilter} onChange={setDueFilter} />
        ) : (
          <TasksOwnershipFilter value={ownership} onChange={setOwnership} />
        )
      }
    >
      <TasksList
        referenceType={referenceType}
        dueFilter={showDueFilter ? dueFilter : undefined}
        ownership={showDueFilter ? undefined : ownership}
      />
    </SidePanelContent>
  );
}
